import axios from 'axios';
import { getAuthHeader } from './authService';
import API_CONFIG from '../config';

const API_URL = API_CONFIG.BASE_URL;
const SALES_ORDERS_URL = `${API_URL}/sales-orders`;
const CUSTOMER_URL = `${API_URL}/customers`;

/**
 * Service for managing sales operations
 */
const salesService = {
  /**
   * Get product batches for a given product (optional location filter)
   * @param {number} productId
   * @param {number|null} locationId
   * @returns {Promise<Array>}
   */
  getProductBatchesByProduct: async (productId, locationId = null, includeInactive = false) => {
    try {
      const params = {};
      if (locationId != null) params.locationId = locationId;
      if (includeInactive) params.includeInactive = true;
      const response = await axios.get(`${API_URL}/product-batches/by-product/${productId}`, {
        params,
        headers: getAuthHeader()
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error(`Error fetching product batches for product ${productId}:`, error);
      return [];
    }
  },

  /**
   * Get all sales orders with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.size - Page size
   * @param {string} params.sort - Field to sort by
   * @param {string} params.direction - Sort direction (ASC or DESC)
   * @returns {Promise<Object>} Paginated sales orders data
   */
  getAllSalesOrders: async (params = { page: 0, size: 10, sort: 'createdAt', direction: 'DESC' }) => {
    try {
      // Add excludeStatus parameter to filter out RETURNED orders
      const queryParams = {
        ...params,
        excludeStatus: 'RETURNED'
      };
      
      const response = await axios.get(SALES_ORDERS_URL, {
        params: queryParams,
        headers: getAuthHeader()
      });
      
      // Handle both paginated and non-paginated responses
      if (response.data && typeof response.data === 'object' && response.data.content) {
        // Return paginated response
        return response.data;
      } else if (Array.isArray(response.data)) {
        // If API returns an array directly (non-paginated)
        // Convert to paginated format for consistent handling in the UI
        return {
          content: response.data,
          totalElements: response.data.length,
          totalPages: 1,
          size: response.data.length,
          number: 0
        };
      } else {
        // Handle unexpected response format
        console.warn('Unexpected response format from sales API:', response.data);
        return { content: [], totalElements: 0, totalPages: 0, size: params.size, number: params.page };
      }
    } catch (error) {
      console.error('Error fetching sales orders:', error);
      throw error;
    }
  },

  /**
   * Get recent sales for a specific product and customer
   * @param {number} productId
   * @param {number} customerId
   * @param {number} limit
   */
  getRecentProductSales: async (productId, customerId, limit = 5) => {
    try {
      const response = await axios.get(`${SALES_ORDERS_URL}/recent-product-sales`, {
        params: { productId, customerId, limit },
        headers: getAuthHeader()
      });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching recent product sales:', error);
      throw error;
    }
  },

  /**
   * Get all sales orders for a customer (used for item history)
   * @param {number} customerId
   * @returns {Promise<Array>} List of SalesOrderDTO.Response
   */
  getSalesOrdersByCustomer: async (customerId) => {
    try {
      const response = await axios.get(`${SALES_ORDERS_URL}/customer/${customerId}`, {
        headers: getAuthHeader()
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching sales orders by customer:', error);
      throw error;
    }
  },

  /**
   * Get a sales order by ID
   * @param {number} id - Sales order ID
   * @returns {Promise<Object>} Sales order details
   */
  getSalesOrderById: async (id) => {
    try {
      const response = await axios.get(`${SALES_ORDERS_URL}/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching sales order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new sales order (POS sale)
   * @param {Object} salesData - Sales order data
   * @returns {Promise<Object>} Created sales order
   */
  createSalesOrder: async (salesData) => {
    try {
      // Validate required fields first to avoid 400 errors
      if (!salesData.orderDate) {
        throw new Error('Order date is required');
      }
      
      if (!salesData.companyId) {
        throw new Error('Company ID is required');
      }
      
      if (!salesData.items || salesData.items.length === 0) {
        throw new Error('Sales order must have at least one item');
      }
      
      // Validate each item
      salesData.items.forEach((item, index) => {
        if (!item.productId) {
          throw new Error(`Item #${index+1}: Product ID is required`);
        }
        if (!item.quantity || item.quantity <= 0) {
          throw new Error(`Item #${index+1}: Quantity must be positive`);
        }
        if (!item.unitPrice || item.unitPrice <= 0) {
          throw new Error(`Item #${index+1}: Unit price must be positive`);
        }
      });
      
      // Ensure we're using the backend expected structure (items, not salesOrderItems)
      const formattedData = {
        ...salesData,
        items: salesData.salesOrderItems || salesData.items,
      };
      
      // Remove the salesOrderItems field if it exists
      if (formattedData.salesOrderItems) {
        delete formattedData.salesOrderItems;
      }
      
      // Ensure all numeric values are properly typed as numbers
      if (formattedData.companyId) {
        formattedData.companyId = Number(formattedData.companyId);
      }
      
      // Make sure each item's fields are properly typed
      formattedData.items = formattedData.items.map(item => ({
        ...item,
        productId: Number(item.productId),
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        taxId: item.taxId ? Number(item.taxId) : null,
        discount: Number(item.discount || 0),
        productBatchId: item.productBatchId ? Number(item.productBatchId) : null
      }));
      
      // Calculate totals if not provided
      if (!formattedData.subtotal) {
        const subtotal = formattedData.items.reduce((sum, item) => 
          sum + (item.unitPrice * item.quantity), 0);
        formattedData.subtotal = Number(subtotal.toFixed(2));
      } else {
        formattedData.subtotal = Number(formattedData.subtotal);
      }
      
      // Calculate tax amount if not provided
      if (!formattedData.taxAmount) {
        // Simple default calculation (can be enhanced later)
        const taxAmount = formattedData.items.reduce((sum, item) => {
          // If item has taxId but no calculated tax amount, assume standard rate
          const taxRate = 0.05; // 5% default rate
          return sum + (item.unitPrice * item.quantity * taxRate);
        }, 0);
        formattedData.taxAmount = Number(taxAmount.toFixed(2));
      } else {
        formattedData.taxAmount = Number(formattedData.taxAmount);
      }
      
      // Calculate total if not provided
      if (!formattedData.total) {
        const discount = formattedData.discount || 0;
        const shippingCost = formattedData.shippingCost || 0;
        formattedData.total = Number((formattedData.subtotal + formattedData.taxAmount - discount + shippingCost).toFixed(2));
      } else {
        formattedData.total = Number(formattedData.total);
      }
      
      // If totalAmount exists, rename to total as expected by backend
      if (formattedData.totalAmount !== undefined) {
        formattedData.total = Number(formattedData.totalAmount);
        delete formattedData.totalAmount;
      }
      
      // Ensure other numeric fields are proper numbers
      if (formattedData.discount !== undefined) {
        formattedData.discount = Number(formattedData.discount || 0);
      }
      
      if (formattedData.shippingCost !== undefined) {
        formattedData.shippingCost = Number(formattedData.shippingCost || 0);
      }

      // Preserve POS round-off: when present, ensure roundOffAmount is sent as number so backend stores rounded total
      if (formattedData.roundOffAmount !== undefined) {
        formattedData.roundOffAmount = Number(formattedData.roundOffAmount);
      }
      
      // Remove any custom fields not expected by the backend
      if (formattedData.updateCreditBalance !== undefined) {
        delete formattedData.updateCreditBalance;
      }
      
      // For credit payment method, ensure paymentStatus is valid
      if (formattedData.paymentMethod === 'CREDIT') {
        // Some backends expect paymentStatus as enum value, not string
        // If the backend is using a numeric enum, convert to string
        formattedData.paymentStatus = "PENDING";
        
        // Note: Status is already set to DELIVERED in POS component for all payment methods
        // to ensure inventory is updated immediately
      }
      
      console.log('Creating sales order with data:', JSON.stringify(formattedData, null, 2));
      console.log('Using endpoint:', SALES_ORDERS_URL);
      
      // Create headers with explicit Content-Type
      const headers = {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      };
      console.log('Headers:', headers);
      
      const response = await axios.post(SALES_ORDERS_URL, formattedData, {
        headers: headers
      });
      
      console.log('Sales order created successfully:', response.data);
      
      // Trigger immediate notification for the new order
      if (response.data && response.data.id) {
        try {
          // Create a local notification for immediate feedback
          const orderNotification = {
            id: `sales-order-${response.data.id}-${Date.now()}`,
            type: 'SALES_ORDER',
            title: `New order #${response.data.orderNumber || response.data.id}`,
            message: `Sales order created successfully for $${response.data.total?.toFixed(2) || '0.00'}`,
            createdAt: new Date().toISOString(),
            read: false,
            data: {
              orderId: response.data.id,
              orderNumber: response.data.orderNumber,
              total: response.data.total || 0,
              customerName: response.data.customer?.name || 'Customer'
            }
          };
          
          // Trigger notification event for immediate UI update
          window.dispatchEvent(new CustomEvent('newNotification', { 
            detail: orderNotification 
          }));
          
          console.log('Immediate notification triggered for new order:', orderNotification);
        } catch (notificationError) {
          console.warn('Failed to create immediate notification:', notificationError);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating sales order:', error.message);
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const errorData = error.response.data || 'No error details provided';
        console.error('Response data:', errorData);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        
        // Enhanced error handling
        if (typeof errorData === 'string' && errorData.trim()) {
          error.message = errorData.trim();
        } 
        else if (errorData && errorData.message) {
          error.message = errorData.message;
        }
        else if (errorData && errorData.error) {
          error.message = errorData.error;
        }
        else if (error.response.status === 400) {
          // For credit payment specifically, provide more detailed error message
          if (salesData.paymentMethod === 'CREDIT') {
            error.message = 'Invalid credit sale data. Please ensure customer ID is valid and all required fields are provided.';
          } else {
            error.message = 'Invalid sales order data. Please check all required fields.';
          }
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Request made but no response received:', error.request);
        error.message = 'No response received from server. Please check your connection.';
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
      }
      
      throw error;
    }
  },

  /**
   * Update a sales order
   * @param {number} id - Sales order ID
   * @param {Object} salesData - Updated sales order data
   * @returns {Promise<Object>} Updated sales order
   */
  updateSalesOrder: async (id, salesData) => {
    try {
      const response = await axios.put(`${SALES_ORDERS_URL}/${id}`, salesData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating sales order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a sales order
   * @param {number} id - Sales order ID
   * @returns {Promise<void>}
   */
  deleteSalesOrder: async (id) => {
    try {
      await axios.delete(`${SALES_ORDERS_URL}/${id}`, {
        headers: getAuthHeader()
      });
    } catch (error) {
      console.error(`Error deleting sales order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Update the status of a sales order
   * @param {number} id - Sales order ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated sales order
   */
  updateSalesOrderStatus: async (id, status) => {
    try {
      // Send status as a query param, not in the body
      const response = await axios.patch(`${SALES_ORDERS_URL}/${id}/status`, null, {
        headers: getAuthHeader(),
        params: { status }
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating status for sales order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Update the payment status of a sales order
   * @param {number} id - Sales order ID
   * @param {string} paymentStatus - New payment status
   * @returns {Promise<Object>} Updated sales order
   */
  updateSalesOrderPaymentStatus: async (id, paymentStatus) => {
    try {
      // Send payment status as a query param, not in the body
      const response = await axios.patch(`${SALES_ORDERS_URL}/${id}/payment-status`, null, {
        headers: getAuthHeader(),
        params: { paymentStatus }
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating payment status for sales order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get all customers with optional pagination
   * @param {Object} params - Query parameters (optional)
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.size - Page size
   * @param {string} params.sort - Field to sort by
   * @param {string} params.direction - Sort direction (ASC or DESC)
   * @param {string} params.status - Customer status filter (default: 'ACTIVE')
   * @returns {Promise<Object|Array>} Paginated customers data or array of customers
   */
  getAllCustomers: async (params = {}) => {
    try {
      // Add status filter to only get active customers by default
      const queryParams = {
        ...params,
        // If no status specified, default to ACTIVE to hide inactive customers
        ...(params.status === undefined && { status: 'ACTIVE' })
      };
      
      const response = await axios.get(CUSTOMER_URL, {
        headers: getAuthHeader(),
        params: queryParams
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  },

  /**
   * Get a customer by ID
   * @param {number} id - Customer ID
   * @returns {Promise<Object>} Customer details
   */
  getCustomerById: async (id) => {
    try {
      const response = await axios.get(`${CUSTOMER_URL}/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching customer with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new customer
   * @param {Object} customerData - Customer data
   * @returns {Promise<Object>} Created customer
   */
  createCustomer: async (customerData) => {
    try {
      const response = await axios.post(CUSTOMER_URL, customerData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  },

  /**
   * Get the items of a sales order
   * @param {number} orderId - Sales order ID
   * @returns {Promise<Array>} List of sales order items
   */
  getSalesOrderItems: async (orderId) => {
    try {
      const response = await axios.get(`${SALES_ORDERS_URL}/${orderId}/items`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching items for sales order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Generate a PDF receipt for a sales order
   * @param {number} orderId - Sales order ID
   * @param {string} receiptType - Type of receipt ('CUSTOMER', 'OFFICE', 'OFFICE_WITH_SIGNATURE')
   * @param {string} customHeader - Optional custom header text for the receipt
   * @param {string} format - Optional print format override
   * @returns {Promise<Blob>} PDF document as a Blob
   */
  generateReceipt: async (orderId, receiptType = 'CUSTOMER', customHeader = null, format = null) => {
    try {
      // Build query parameters
      const params = { type: receiptType };
      
      // Add custom header if provided
      if (customHeader) {
        params.header = customHeader;
      }

      // Add format if provided
      if (format) {
        params.format = format;
      }
      
      const response = await axios.get(`${SALES_ORDERS_URL}/${orderId}/receipt`, {
        headers: getAuthHeader(),
        responseType: 'blob',
        params: params
      });
      return response.data;
    } catch (error) {
      console.error(`Error generating receipt for sales order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Generate multiple PDF receipts for a sales order
   * @param {number} orderId - Sales order ID
   * @param {Array<string>} receiptTypes - Array of receipt types to generate
   * @returns {Promise<Array<{type: string, blob: Blob}>>} Array of PDF documents as Blobs with their types
   */
  generateMultipleReceipts: async (orderId, receiptTypes) => {
    try {
      // Generate receipts in parallel with appropriate headers for each type
      const receiptsPromises = receiptTypes.map(type => {
        // Define custom header based on receipt type
        let customHeader;
        switch (type) {
          case 'CUSTOMER':
            customHeader = null; // Removed 'CUSTOMER COPY' - use default title
            break;
          case 'OFFICE':
            customHeader = null; // Removed 'OFFICE COPY' - use default title
            break;
          case 'OFFICE_WITH_SIGNATURE':
            customHeader = null; // Removed 'OFFICE COPY - CUSTOMER SIGNATURE REQUIRED' - use default title
            break;
          default:
            customHeader = null;
        }
        
        // Generate receipt with custom header
        return salesService.generateReceipt(orderId, type, customHeader)
          .then(blob => ({ type, blob }));
      });
      
      return await Promise.all(receiptsPromises);
    } catch (error) {
      console.error(`Error generating multiple receipts for sales order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Process a sales return
   * @param {number} orderId - Sales order ID
   * @param {Object} returnData - Return data including items, reason, and notes
   * @returns {Promise<Object>} Processed return data
   */
  processSalesReturn: async (orderId, returnData) => {
    try {
      const response = await axios.post(`${SALES_ORDERS_URL}/${orderId}/returns`, returnData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error processing return for order ID ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Fetch return data from the backend including return reason and date
   * @returns {Promise<Object>} Return data with return details
   */
  fetchReturnData: async () => {
    try {
      const response = await axios.get(`${SALES_ORDERS_URL}/status/RETURNED`, {
        headers: getAuthHeader()
      });
      
      const data = response.data;
      
      // Log the response data for debugging
      console.log('Returned orders data:', data);
      
      return data;
    } catch (error) {
      console.error('Error fetching return data:', error);
      throw error;
    }
  },

  /**
   * Get all returned orders
   * @returns {Promise<Array>} List of returned orders
   */
  getReturnedOrders: async () => {
    try {
      const response = await axios.get(`${SALES_ORDERS_URL}/status/RETURNED`, {
        headers: getAuthHeader()
      });
      console.log('🔍 [DEBUG] API Response Status:', response.status);
      console.log('🔍 [DEBUG] API Response Data:', response.data);
      console.log('🔍 [DEBUG] Response Data Type:', typeof response.data);
      console.log('🔍 [DEBUG] Is Array?', Array.isArray(response.data));
      if (Array.isArray(response.data) && response.data.length > 0) {
        console.log('🔍 [DEBUG] First Return Order:', response.data[0]);
        console.log('🔍 [DEBUG] First Order Status:', response.data[0].status);
        console.log('🔍 [DEBUG] First Order OrderNumber:', response.data[0].orderNumber);
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching returned orders:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  /**
   * Get sales orders within a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} List of sales orders within the date range
   */
  getSalesOrdersByDateRange: async (startDate, endDate) => {
    try {
      const response = await axios.get(`${SALES_ORDERS_URL}/date-range`, {
        params: { startDate, endDate },
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching sales orders in date range ${startDate} to ${endDate}:`, error);
      throw error;
    }
  },

  /**
   * Get previously returned quantities for an order
   * @param {number} orderId - Original sales order ID
   * @returns {Promise<Object>} Map of product IDs to returned quantities
   */
  getPreviouslyReturnedQuantities: async (orderId) => {
    try {
      // Get the original order to find the order number format
      const originalOrder = await salesService.getSalesOrderById(orderId);
      const originalOrderNumber = originalOrder.orderNumber;
      
      if (!originalOrderNumber) {
        console.error(`No order number found for order ID ${orderId}`);
        return {};
      }
      
      console.log(`Fetching return data for order: ${originalOrderNumber} (ID: ${orderId})`);
      
      // Try multiple approaches to find returns
      const returnedQuantities = {};
      
      // Check for RETURN transactions directly in inventory transactions
      try {
        const transactionResponse = await axios.get(`${API_URL}/inventory-transactions/by-reference`, {
          params: { 
            orderReference: originalOrderNumber,
            type: 'RETURN'
          },
          headers: getAuthHeader()
        });
        
        console.log(`Found ${transactionResponse.data.length} return transactions for order ${originalOrderNumber}`);
        
        // Process inventory transactions
        for (const transaction of transactionResponse.data) {
          const productId = transaction.productId;
          if (productId) {
            returnedQuantities[productId] = (returnedQuantities[productId] || 0) + transaction.quantity;
            console.log(`Found product ${productId} with returned quantity ${transaction.quantity}`);
          }
        }
      } catch (error) {
        console.warn(`Error fetching inventory transactions for ${originalOrderNumber}:`, error.message);
      }
      
      console.log(`Final returned quantities for order ${originalOrderNumber}:`, returnedQuantities);
      return returnedQuantities;
    } catch (error) {
      console.error(`Error fetching previously returned quantities for order ${orderId}:`, error);
      return {};
    }
  },

  /**
   * Update a customer's credit balance
   * @param {number} customerId - Customer ID
   * @param {number} newCreditBalance - New credit balance amount
   * @returns {Promise<Object>} Updated customer
   */
  updateCustomerCreditBalance: async (customerId, newCreditBalance) => {
    try {
      if (!customerId) {
        throw new Error('Customer ID is required for credit balance update');
      }
      
      if (isNaN(newCreditBalance)) {
        throw new Error('Credit balance must be a valid number');
      }
      
      console.log(`Updating credit balance for customer ${customerId} to ${newCreditBalance}`);
      
      // Ensure credit balance is formatted as a number
      const formattedBalance = Number(newCreditBalance);
      
      const customerData = {
        creditBalance: formattedBalance
      };
      
      console.log('Credit balance update payload:', JSON.stringify(customerData));
      
      const response = await axios.patch(`${CUSTOMER_URL}/${customerId}/credit-balance`, customerData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Credit balance update response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error updating credit balance for customer ${customerId}:`, error);
      
      // Enhanced error logging
      if (error.response) {
        console.error('Credit balance update error details:', {
          status: error.response.status,
          data: error.response.data || 'No error details provided',
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  /**
   * Convert a quotation to a normal bill
   * @param {number} quotationId - The ID of the quotation to convert
   * @param {Object} updateData - Optional data to update items/quantities before conversion
   * @returns {Promise<Object>} The converted sales order
   */
  convertQuotationToBill: async (quotationId, updateData = null) => {
    try {
      const response = await axios.post(
        `${SALES_ORDERS_URL}/${quotationId}/convert-to-bill`,
        updateData,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error(`Error converting quotation ${quotationId} to bill:`, error);
      throw error;
    }
  },

  /**
   * Get the next expected order number
   * @param {boolean} nonGst - Whether to get next number for Non-GST mode
   * @returns {Promise<string>} Next order number
   */
  getNextOrderNumber: async (nonGst = false) => {
    try {
      const response = await axios.get(`${SALES_ORDERS_URL}/next-order-number`, {
        params: { nonGst },
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching next order number:', error);
      return '';
    }
  },
};

export default salesService; 